/**
 * Disease Tracking Service
 * CRUD operations for DiseaseRecords collection
 * Supports California Faecal Scoring and Wisconsin Calf Health Scoring
 *
 * @collection Users/{userId}/DiseaseRecords
 */

import { db } from "../../../firebase";

// ============================================================================
// WRITE OPERATIONS
// ============================================================================

/**
 * Add a disease record
 * @param {string} userId
 * @param {string} mac - Animal MAC
 * @param {Object} data - { diseaseCategory, severity, faecalScore, wisconsinScore{}, symptoms[], outcome, notes }
 * @returns {Promise<string>} Document ID
 */
export async function addDiseaseRecord(userId, mac, data) {
	const timestamp = Date.now();
	const docId = timestamp.toString();

	const cleanData = Object.fromEntries(
		Object.entries(data).filter(([, v]) => v !== undefined)
	);

	// Calculate total Wisconsin score if components provided
	if (cleanData.wisconsinScore) {
		const ws = cleanData.wisconsinScore;
		cleanData.wisconsinScoreTotal =
			(ws.eyes || 0) + (ws.ears || 0) + (ws.nasal || 0) + (ws.cough || 0);
	}

	const record = {
		...cleanData,
		MAC: mac,
		Uid: userId,
		t: timestamp,
		Archived: false,
	};

	await db
		.collection("Users")
		.doc(userId)
		.collection("DiseaseRecords")
		.doc(docId)
		.set(record);

	return docId;
}

/**
 * Update a disease record
 */
export async function updateDiseaseRecord(userId, docId, updates) {
	const cleanUpdates = Object.fromEntries(
		Object.entries(updates).filter(([, v]) => v !== undefined)
	);

	if (cleanUpdates.wisconsinScore) {
		const ws = cleanUpdates.wisconsinScore;
		cleanUpdates.wisconsinScoreTotal =
			(ws.eyes || 0) + (ws.ears || 0) + (ws.nasal || 0) + (ws.cough || 0);
	}

	await db
		.collection("Users")
		.doc(userId)
		.collection("DiseaseRecords")
		.doc(docId)
		.update({ ...cleanUpdates, updatedAt: Date.now() });
}

/**
 * Archive a disease record
 */
export async function archiveDiseaseRecord(userId, docId) {
	await updateDiseaseRecord(userId, docId, { Archived: true, ArchivedAt: Date.now() });
}

/**
 * Update disease outcome (resolved, ongoing, etc.)
 */
export async function updateDiseaseOutcome(userId, docId, outcome) {
	await updateDiseaseRecord(userId, docId, { outcome, outcomeDate: Date.now() });
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Get all disease records for an animal
 */
export async function getDiseaseRecordsByAnimal(userId, mac) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("DiseaseRecords")
		.where("MAC", "==", mac)
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get all disease records for a user
 */
export async function getAllDiseaseRecords(userId) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("DiseaseRecords")
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get active (ongoing) disease cases
 */
export async function getActiveDiseaseRecords(userId) {
	const all = await getAllDiseaseRecords(userId);
	return all.filter((r) => !r.outcome || r.outcome === "ongoing");
}

/**
 * Get disease records by category
 */
export async function getDiseaseRecordsByCategory(userId, category) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("DiseaseRecords")
		.where("diseaseCategory", "==", category)
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

// ============================================================================
// SCORING HELPERS
// ============================================================================

/**
 * Classify faecal score severity
 * @param {number} score - 0-3 California Faecal Score
 * @returns {string} "normal" | "mild" | "moderate" | "severe"
 */
export function classifyFaecalScore(score) {
	if (score === 0) return "normal";
	if (score === 1) return "mild";
	if (score === 2) return "moderate";
	return "severe";
}

/**
 * Classify Wisconsin score severity
 * @param {Object} scores - { eyes, ears, nasal, cough } (0-3 each)
 * @returns {{ total: number, severity: string, flagged: Array<string> }}
 */
export function classifyWisconsinScore(scores) {
	const total = (scores.eyes || 0) + (scores.ears || 0) + (scores.nasal || 0) + (scores.cough || 0);
	const flagged = [];

	if (scores.eyes >= 2) flagged.push("eyes");
	if (scores.ears >= 2) flagged.push("ears");
	if (scores.nasal >= 2) flagged.push("nasal");
	if (scores.cough >= 2) flagged.push("cough");

	let severity;
	if (total <= 2) severity = "normal";
	else if (total <= 4) severity = "mild";
	else if (total <= 8) severity = "moderate";
	else severity = "severe";

	return { total, severity, flagged };
}
